import React from "react";
import { Container, Row, Col, Card, Button, Badge } from "react-bootstrap";
import { useWhitelabel } from "../../contexts/WhitelabelContext";

const PRIVACY_TEXT = `Privacy Policy
M2M Connectivity Platform
This Privacy Policy describes how CLICKSIMS S.A ("Provider") collects, uses, and protects personal data in connection with the M2M Connectivity Platform and related services ("Services"). This policy is compliant with the General Data Protection Regulation (EU) 2016/679 ("GDPR") and other applicable data protection laws.
1. Categories of Personal Data
1.1 We process the following categories of personal data:
Contact Information
•	Name and surname
•	Postal address
•	Email address
•	Telephone number
Identification Information
•	VAT number
•	Platform username
Demographic Information
•	Location
•	Language preferences
Device and Technical Data
•	IP address
•	Device type, brand, make, and model
•	SIM card and connectivity usage data
Operational Data
•	Account Activity Logs for Operational and Debugging purposes

1.2 We do not process special categories of personal data (such as health data, racial or ethnic origin, political opinions, religious beliefs, genetic or biometric data).
2. Purposes of Processing
2.1 We process personal data for the following purposes:
•	Providing and managing our M2M Connectivity Platform and Services
•	Account creation and management
•	Processing payments and billing
•	Customer support and technical assistance
•	Service improvement and analytics
•	Security monitoring and fraud prevention
•	Compliance with legal obligations
•	Communication about service updates and changes
3. Legal Basis for Processing
3.1 We process personal data based on the following legal grounds:
(a) Contract Performance: Processing necessary for the performance of our contract with you to provide the Services.
(b) Legal Obligation: Processing necessary for compliance with legal obligations to which we are subject.
(c) Legitimate Interests: Processing necessary for our legitimate interests, including service improvement, security, and fraud prevention, where these interests are not overridden by your rights.
(d) Consent: Where required, we will obtain your consent for specific processing activities.
4. Data Retention
4.1 We retain personal data only for as long as necessary to fulfill the purposes for which it was collected, including satisfying legal, accounting, or reporting requirements.
4.2 Retention periods depend on the nature of the data and processing purposes:
•	Account data: Duration of the contractual relationship plus 1 year
•	Billing records: 1 year for tax and accounting purposes
•	Usage logs: 3 months for service optimization
•	Support tickets: 6 months after resolution
5. Data Sharing and Recipients
5.1 We may share personal data with:
(a) Sub-processors: Third-party service providers who assist in delivering our Services, bound by data protection obligations at least as restrictive as those in this Policy.
(b) Mobile Network Operators: For connectivity services and roaming purposes.
(c) Professional Advisors: Lawyers, accountants, and auditors as necessary.
(d) Authorities: Government bodies, regulators, or law enforcement when required by law.
5.2 We will inform you of changes to our sub-processor list with at least one week's advance notice, giving you the opportunity to object.
6. International Data Transfers
6.1 Personal data may be transferred to countries outside the European Economic Area (EEA) for processing by sub-processors or network partners.
6.2 Such transfers are protected by appropriate safeguards including:
•	Standard Contractual Clauses approved by the European Commission
•	Adequacy decisions where applicable
•	Binding Corporate Rules where implemented
7. Your Rights
7.1 Under applicable data protection laws, you have the following rights:
(a) Right of Access: Request a copy of your personal data we hold.
(b) Right to Rectification: Request correction of inaccurate or incomplete data.
(c) Right to Erasure: Request deletion of your personal data in certain circumstances.
(d) Right to Restrict Processing: Request limitation of processing in certain circumstances.
(e) Right to Data Portability: Receive your data in a structured, machine-readable format.
(f) Right to Object: Object to processing based on legitimate interests or for direct marketing.
(g) Right to Withdraw Consent: Where processing is based on consent, withdraw it at any time.
7.2 To exercise these rights, contact us using the details in Section 11. We will respond within one month, extendable by up two months for complex requests.
8. Data Security
8.1 We implement appropriate technical and organizational measures to protect personal data against unauthorized access, alteration, disclosure, or destruction.
8.2 These measures include:
•	Encryption of data in transit and at rest
•	Access controls and authentication mechanisms
•	Regular security assessments and audits
•	Staff training on data protection
•	Confidentiality obligations for personnel
8.3 Access to personal data is limited to personnel who need it for their work, and all such personnel are bound by confidentiality obligations.
9. Data Breach Notification
9.1 In the event of a personal data breach, we will:
(a) Notify the relevant supervisory authority within 72 hours of becoming aware of the breach, where required.
(b) Notify affected data subjects without undue delay if the breach is likely to result in high risk to their rights and freedoms.
9.2 Notifications will include:
•	Description of the nature of the breach
•	Categories and approximate number of data subjects concerned
•	Likely consequences of the breach
•	Measures taken or proposed to address the breach
10. Changes to This Policy
10.1 We may update this Privacy Policy from time to time. Material changes will be notified to you via email or through the Platform.
11. Contact Information
For questions about this Privacy Policy or to exercise your data protection rights, contact:
Company: CLICKSIMS S.A 
Data Protection Officer: Mr. Adamantios Lefkas
Address: Perikleous 0, Thessaloniki, Greece
Email: support@clicksims.com
Phone: +302313082163`;

const PrivacyPolicy = () => {
  const paragraphs = React.useMemo(() => PRIVACY_TEXT.split(/\n\s*\n/), []);

  return (
    <div className="bg-light min-vh-100 py-5">
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={8}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-1">M2M Connectivity Platform</h2>
                <div className="text-muted small d-flex align-items-center gap-2">
                  <Badge bg="primary" pill>
                    Privacy Policy
                  </Badge>
                  <span>Data Protection &amp; GDPR</span>
                </div>
              </div>
              {/* <div className="d-none d-md-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleDownloadPdf}
                >
                  PDF (with watermark)
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleDownloadDocx}
                >
                  DOCX
                </Button>
              </div> */}
            </div>

            <Card className="shadow border-0 rounded-4 overflow-hidden">
              <Card.Header className="bg-white border-0 py-3 px-4">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                  <div>
                    <div className="fw-semibold">Privacy Policy</div>
                    <div className="text-muted small">
                      How we collect, use, and protect your personal data.
                    </div>
                  </div>
                </div>
              </Card.Header>

              <Card.Body
                className="px-4 pt-3 pb-4"
                style={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                  background:
                    "radial-gradient(circle at top, #ffffff 0, #f9fafb 45%, #f3f4f6 100%)",
                }}
              >
                <div
                  style={{
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    color: "#212529",
                  }}
                >
                  {paragraphs.map((paragraph, idx) => (
                    <div
                      key={idx}
                      className="mb-3 pb-2"
                      style={{
                        borderLeft:
                          idx === 0
                            ? "4px solid rgba(13,110,253,.45)"
                            : "1px solid rgba(0,0,0,0.03)",
                        paddingLeft: "0.9rem",
                        backgroundColor:
                          idx === 0 ? "rgba(13,110,253,0.02)" : "transparent",
                        borderRadius: "0 0.5rem 0.5rem 0",
                      }}
                    >
                      <p className="mb-0">
                        {paragraph.split("\n").map((line, i, arr) => (
                          <React.Fragment key={i}>
                            {line}
                            {i < arr.length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </p>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;
